/**
 * DoneStep — final step of the onboarding wizard.
 *
 * Celebrates completion, surfaces three "what next" links, and hosts the
 * primary CTA that calls POST /api/v1/users/complete-onboarding before
 * navigating to the dashboard. Onboarding completion is a UX nicety, NOT a
 * gate — if the API call fails the user is still routed home.
 */

import { useCallback, useState, type RefObject } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { Button } from '@/components/ui'

type DoneStepProps = {
  headingRef?: RefObject<HTMLHeadingElement>
}

export function DoneStep({ headingRef }: DoneStepProps = {}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)

  const handleFinish = useCallback(async () => {
    setSubmitting(true)
    try {
      await api.completeOnboarding()
    } catch (error) {
      // Graceful degradation: completion is non-blocking. We log and proceed.
      console.warn('completeOnboarding failed', error)
    } finally {
      // Invalidate the cached profile so ProtectedRoute sees the updated
      // onboardingCompletedAt and doesn't redirect back to /onboarding.
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      navigate('/')
    }
  }, [navigate, queryClient])

  return (
    <section
      role="form"
      aria-label="Setup complete"
      className="max-w-2xl mx-auto text-center space-y-8 py-12"
    >
      <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-success-100">
        <svg
          className="h-12 w-12 text-success-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <div className="space-y-3">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-4xl font-bold text-gray-900 tracking-tight focus:outline-none"
        >
          You&apos;re all set!
        </h2>
        <p className="text-lg text-gray-600">
          Your canary is watching. When someone copies your page, you&apos;ll
          see a detection appear on your dashboard.
        </p>
      </div>

      <ul className="text-left space-y-3 max-w-md mx-auto">
        <li>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            Check your dashboard for detections
          </Link>
        </li>
        <li>
          <Link to="/canaries/new" className="text-primary-600 hover:text-primary-700 font-medium">
            Add more canaries to protect other pages
          </Link>
        </li>
        <li>
          <Link to="/settings" className="text-primary-600 hover:text-primary-700 font-medium">
            Configure alert recipients
          </Link>
        </li>
      </ul>

      <div className="pt-4">
        <Button
          size="lg"
          isLoading={submitting}
          onClick={() => { void handleFinish() }}
          className="w-full sm:w-auto min-h-[44px]"
        >
          Go to Dashboard
        </Button>
      </div>
    </section>
  )
}
